/**
 * Utility di VISUALIZZAZIONE della Readiness.
 * Le SOGLIE arrivano da @leopart/calc (fonte unica: 70/50/30, le stesse del
 * backend) — qui vive solo la mappatura status → colore/etichetta.
 * Prima di calc: questo file usava 70/40 e 80/60/40, altre pagine 90/75/60,
 * il mobile 75/55 — lo stesso RI aveva 4 colori diversi nella piattaforma.
 */
import { classifyRI, RiStatus } from '@leopart/calc';

export type ReadinessColor = 'green' | 'yellow' | 'red';
export type ReadinessLabel = 'Ottimo' | 'Buono' | 'Sufficiente' | 'Scarso';

const STATUS_COLOR: Record<RiStatus, ReadinessColor> = {
  good: 'green',
  normal: 'yellow',
  warning: 'yellow',
  critical: 'red',
};

const STATUS_LABEL: Record<RiStatus, ReadinessLabel> = {
  good: 'Ottimo',
  normal: 'Buono',
  warning: 'Sufficiente',
  critical: 'Scarso',
};

export function getReadinessColor(
  value: number | null | undefined,
): ReadinessColor {
  if (value == null || isNaN(value as number)) return 'red';
  return STATUS_COLOR[classifyRI(value)];
}

export function getReadinessLabel(
  value: number | null | undefined,
): ReadinessLabel {
  if (value == null || isNaN(value as number)) return 'Scarso';
  return STATUS_LABEL[classifyRI(value)];
}

export function getReadinessStatus(
  value: number | null | undefined,
): 'good' | 'medium' | 'poor' {
  if (value == null || isNaN(value as number)) return 'poor';
  const s = classifyRI(value);
  return s === 'good' ? 'good' : s === 'critical' ? 'poor' : 'medium';
}

/** True se il valore di readiness è nella fascia "good" (≥ 70). */
export function isReadinessGood(value: number | null | undefined): boolean {
  return getReadinessStatus(value) === 'good';
}

/**
 * Formatta un valore di readiness come stringa intera (es. "75").
 * Restituisce "—" per valori null/undefined/NaN.
 */
export function formatReadinessValue(value: number | null | undefined): string {
  if (value == null || isNaN(value as number)) return '—';
  return Math.round(value).toString();
}

/**
 * Calcola la media di un team ignorando null/undefined/NaN.
 * Restituisce 0 se non ci sono valori validi.
 */
export function calculateTeamAverage(values: (number | null | undefined)[]): number {
  const valid = values.filter((v): v is number => v != null && !isNaN(v));
  if (valid.length === 0) return 0;
  return valid.reduce((sum, v) => sum + v, 0) / valid.length;
}

export interface ReadinessComponents {
  mentale?: number | null;
  muscolare?: number | null;
  cardio?: number | null;
  strain?: number | null;
}

/**
 * Calcola l'indice composito di readiness da 4 componenti con pesi uguali.
 * Ignora componenti null/undefined.
 */
export function calculateReadinessIndex(components: ReadinessComponents): number | null {
  const values = [components.mentale, components.muscolare, components.cardio, components.strain]
    .filter((v): v is number => v != null && !isNaN(v));
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/**
 * Formatta un valore come percentuale (es. "75%").
 */
export function formatReadinessPercent(value: number | null | undefined): string {
  if (value == null || isNaN(value as number)) return '—';
  return `${Math.round(value)}%`;
}

/**
 * Calcola il trend rispetto a una baseline.
 * Restituisce 'improving' | 'declining' | 'stable'.
 */
export function calculateReadinessTrend(
  current: number | null | undefined,
  baseline: number | null | undefined,
  threshold = 5,
): 'improving' | 'declining' | 'stable' {
  if (current == null || baseline == null || isNaN(current) || isNaN(baseline)) return 'stable';
  const delta = current - baseline;
  if (delta >= threshold) return 'improving';
  if (delta <= -threshold) return 'declining';
  return 'stable';
}

/**
 * Colore badge mappato da colore readiness a hex/stringa CSS.
 */
export function getReadinessBadgeColor(color: ReadinessColor): string {
  const map: Record<ReadinessColor, string> = {
    green: '#22c55e',
    yellow: '#f59e0b',
    red: '#ef4444',
  };
  return map[color];
}
